import React, { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";

interface TagModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: (name: string, color: string) => Promise<void>;
  initialName?: string;
  initialColor?: string;
  mode?: "create" | "edit";
}

const PRESET_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
];

const TagModal: React.FC<TagModalProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  initialName = "",
  initialColor = "#3B82F6",
  mode = "create",
}) => {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setColor(initialColor);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, initialName, initialColor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Tag name is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(name.trim(), color);
      setIsOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save tag");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          {mode === "create" ? "Create new label" : "Edit label"}
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          {mode === "create" ? "Add a label to organise your documents." : "Update the label name or colour."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="tag-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Label name
            </label>
            <input
              id="tag-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Lab notes"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50"
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Label colour
            </label>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
              {PRESET_COLORS.map((presetColor) => (
                <button
                  key={presetColor}
                  type="button"
                  onClick={() => setColor(presetColor)}
                  className={`aspect-square w-full rounded-md transition-all ${
                    color === presetColor
                      ? "ring-2 ring-offset-1 ring-gray-900"
                      : "ring-1 ring-gray-200 hover:ring-gray-400"
                  }`}
                  style={{ backgroundColor: presetColor }}
                  disabled={isSubmitting}
                  aria-label={`Select color ${presetColor}`}
                />
              ))}
              <button
                type="button"
                onClick={() => colorInputRef.current?.click()}
                className="aspect-square w-full rounded-md ring-1 ring-gray-200 hover:ring-gray-400 transition-all flex items-center justify-center bg-white"
                disabled={isSubmitting}
                aria-label="Choose custom color"
              >
                <Plus className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <input
              ref={colorInputRef}
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="absolute opacity-0 pointer-events-none"
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex-1"
            >
              {isSubmitting ? "Saving..." : mode === "create" ? "Create label" : "Save changes"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TagModal;
